// import { CustomPropertiesEntry } from '../../../../../types.generated';
import { formatTimestampToReadable } from '../../../../shared/time/timeUtils';
import EntityRegistry from '../../../EntityRegistry';
import { GenericEntityProperties } from '../../types';
import { PropertyRow, ValueColumnData } from './types';

const DATA_SYSTEM_KEY = 'data_system';

/**
 *
 * @param value - The value of the data system property has to be a link to the data system entity.
 * @returns - The value of the data system property has to be a link to the data system entity with the current host.
 * Example:
 * Input: https://datahub.example.com/data-system/kafka
 * Output: https://app.securiti.xyz/#/data-system/kafka
 */

function remakeDataSystemValue(value: string) {
    let currentHost = window.location.origin;

    // Remove 'edc.' prefix from the host if it exists
    const parts = currentHost.split('edc.');
    if (parts.length > 1) {
        currentHost = parts.join('');
    }

    const endpoint = value.split('#', 2)[1];

    return `${currentHost}/#${endpoint}`;
}

export function mapCustomPropertiesToPropertyRows(customProperties, isCatalogEntity?: boolean) {
    return (customProperties?.map((customProp) => {
        const isKeyEditable = isCatalogEntity || customProp.isEditable;
        let formattedValue = formatTimestampToReadable(customProp.value || '');

        if (customProp.key === DATA_SYSTEM_KEY) {
            formattedValue = remakeDataSystemValue(customProp.value);
        }
        return {
            displayName: customProp.key,
            values: [{ value: formattedValue }],
            key: isKeyEditable ? customProp.key : '',
            value: customProp.isEditable ? customProp.value : '',
            type: { type: 'string', nativeDataType: 'string' },
            isEditable: customProp.isEditable,
        };
    }) || []) as PropertyRow[];
}

function matchesName(name: string, filterText: string) {
    return name.toLocaleLowerCase().includes(filterText.toLocaleLowerCase());
}

function matchesAnyFromValues(values: ValueColumnData[], filterText: string, entityRegistry: EntityRegistry) {
    return values.some(
        (value) =>
            matchesName(value.value?.toString() || '', filterText) ||
            matchesName(value.entity ? entityRegistry.getDisplayName(value.entity.type, value.entity) : '', filterText),
    );
}

export function getFilteredCustomProperties(filterText: string, entityData?: GenericEntityProperties | null) {
    return entityData?.customProperties?.filter((property) => {
        const formattedValue = formatTimestampToReadable(property.value || '');
        return matchesName(property.key, filterText) || matchesName(formattedValue, filterText);
    });
}

export function filterStructuredProperties(
    entityRegistry: EntityRegistry,
    propertyRows: PropertyRow[],
    filterText?: string,
) {
    if (!propertyRows) return { filteredRows: [], expandedRowsFromFilter: new Set() };
    if (!filterText) return { filteredRows: propertyRows, expandedRowsFromFilter: new Set() };
    const formattedFilterText = filterText.toLocaleLowerCase();

    const finalQualifiedNames = new Set<string>();
    const expandedRowsFromFilter = new Set<string>();

    propertyRows.forEach((row) => {
        // if we match on the qualified name (maybe from a parent) do not filter out
        if (matchesName(row.qualifiedName, formattedFilterText)) {
            finalQualifiedNames.add(row.qualifiedName);
        }
        // if we match specifically on this property (not just its parent), add and expand all parents
        if (
            matchesName(row.displayName, formattedFilterText) ||
            matchesAnyFromValues(row.values || [], formattedFilterText, entityRegistry)
        ) {
            finalQualifiedNames.add(row.qualifiedName);

            const splitFieldPath = row.qualifiedName.split('.');
            splitFieldPath.reduce((previous, current) => {
                finalQualifiedNames.add(previous);
                expandedRowsFromFilter.add(previous);
                return `${previous}.${current}`;
            });
        }
    });

    const filteredRows = propertyRows.filter((row) => finalQualifiedNames.has(row.qualifiedName));

    return { filteredRows, expandedRowsFromFilter };
}
